import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "~/utils/trpc";
import { useToast } from "../ToastProvider";

interface CreateTeamModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (teamId: string) => void;
}

export default function CreateTeamModal({
  open,
  onClose,
  onCreated,
}: CreateTeamModalProps) {
  const { t } = useTranslation();
  const { showActionToast } = useToast();
  const utils = trpc.useUtils();

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const createMutation = trpc.team.create.useMutation({
    onSuccess: async (team) => {
      showActionToast({ title: t("team.createSuccess"), description: t("team.createSuccessDesc", { name: team.name }), severity: "success" });
      await utils.team.list.invalidate();
      setName("");
      onCreated?.(team.id);
      onClose();
    },
    onError: () => showActionToast({ title: t("team.createFailed"), severity: "error" }),
  });

  const handleSubmit = () => {
    if (!name.trim()) {
      setNameError(t("team.nameRequired"));
      return;
    }
    setNameError("");
    createMutation.mutate({
      name: name.trim(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const handleClose = () => {
    setName("");
    setNameError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        handleClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {t("team.create")}
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label={t("common.close")}
            sx={{
              color: "custom.gray.700",
              bgcolor: "transparent",
              "&:hover": {
                color: "custom.primaryHover",
                bgcolor: "transparent",
              },
              "&:active": {
                color: "custom.primaryPressed",
                bgcolor: "transparent",
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("team.createDescription")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label={t("team.name")}
            placeholder={t("team.namePlaceholder")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError("");
            }}
            error={Boolean(nameError)}
            helperText={nameError}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            autoFocus
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={handleClose}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={!name.trim() || createMutation.isPending}
        >
          {t("team.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
